import { useEffect, useMemo, useRef, useState } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { currencies } from '../../utils/currencies';

function CurrencySwitcher() {
  const { currencyCode, setCurrencyCode, currency } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const { priority, rest } = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = query
      ? currencies.filter((item) => item.code.toLowerCase().includes(query) || item.name.toLowerCase().includes(query))
      : currencies;
    return {
      priority: list.filter((item) => item.priority),
      rest: list.filter((item) => !item.priority).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [search]);

  const handleSelect = (code) => {
    setCurrencyCode(code);
    setIsOpen(false);
    setSearch('');
  };

  const renderOption = (item) => (
    <button
      type="button"
      key={item.code}
      className={`currency-switcher-option${item.code === currencyCode ? ' active' : ''}`}
      onClick={() => handleSelect(item.code)}
    >
      <span className="currency-switcher-option-flag" aria-hidden="true">{item.flag}</span>
      <span className="currency-switcher-option-symbol">{item.symbol}</span>
      <span className="currency-switcher-option-code">{item.code}</span>
      <span className="currency-switcher-option-name">{item.name}</span>
    </button>
  );

  return (
    <div className="currency-switcher" ref={containerRef}>
      <button type="button" className="currency-switcher-btn" onClick={() => setIsOpen((open) => !open)} aria-label="Change currency" title="Change currency">
        <span className="currency-switcher-symbol">{currency.symbol}</span>
        <span className="currency-switcher-code">{currency.code}</span>
        <span className="currency-switcher-caret" aria-hidden="true">▾</span>
      </button>
      {isOpen && (
        <div className="currency-switcher-panel">
          <input
            autoFocus
            className="currency-switcher-search"
            placeholder="Search currency..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className="currency-switcher-list">
            {priority.length > 0 && (
              <>
                <div className="currency-switcher-group-label">Most used</div>
                {priority.map(renderOption)}
                {rest.length > 0 && <div className="currency-switcher-divider" />}
              </>
            )}
            {rest.map(renderOption)}
            {priority.length === 0 && rest.length === 0 && (
              <div className="currency-switcher-empty">No currency found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CurrencySwitcher;
